// src/components/traffic/RealtimeTraffic.js - Versión con tabla estable
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Grid, Card, CardHeader, CardContent, Typography,
    Avatar, Button, TableContainer, Table, TableHead,
    TableBody, TableRow, TableCell, Paper, Chip, Tooltip,
    IconButton, LinearProgress
} from '@mui/material';
import {
    CloudDownload, CloudUpload, BarChart, Refresh as RefreshIcon,
    Visibility as ViewIcon, CheckCircle as StatusIcon
} from '@mui/icons-material';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Legend, Tooltip as RechartsTooltip
} from 'recharts';
import { useStableSocket } from '../../hooks/useStableSocket';
import { get } from '../../services/apiService';

// Renderizado de fila estable
const ClientRow = React.memo(({ client, formatRate, formatBytes, onViewDetails }) => {
    // Determinar valores para download y upload
    const downloadRate = client.rateDownload || '0';
    const uploadRate = client.rateUpload || '0';

    // Calcular total bytes
    const totalBytes = (parseInt(client.downloadBytes || 0) + parseInt(client.uploadBytes || 0));

    return (
        <TableRow hover>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={client.status === 'active' ? 'Activo' : 'Inactivo'}>
                        <StatusIcon
                            sx={{
                                fontSize: 15,
                                mr: 1,
                                color: client.status === 'active' ? 'success.main' : 'error.main'
                            }}
                        />
                    </Tooltip>
                    <Typography variant="body2">
                        {client.fullName || client.clientName || client.queueName}
                    </Typography>
                </Box>
            </TableCell>
            <TableCell>{client.ipAddress || '-'}</TableCell>
            <TableCell align="right">
                <Chip
                    size="small"
                    label={formatRate(downloadRate)}
                    sx={{ backgroundColor: 'rgba(63, 81, 181, 0.1)', fontWeight: 500 }}
                />
            </TableCell>
            <TableCell align="right">
                <Chip
                    size="small"
                    label={formatRate(uploadRate)}
                    sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', fontWeight: 500 }}
                />
            </TableCell>
            <TableCell align="right">
                <Chip
                    size="small"
                    label={formatBytes(totalBytes)}
                    sx={{ fontWeight: 500 }}
                />
            </TableCell>
            <TableCell align="center">
                <Tooltip title="Ver detalles">
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onViewDetails(client.id)}
                    >
                        <ViewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
});

// Componente para visualización de tráfico en tiempo real con manejo estable de sockets
function RealtimeTraffic({ clientId, setError, onViewDetails }) {
    // Estados para datos
    const [realtimeData, setRealtimeData] = useState(null);
    const [trafficHistory, setTrafficHistory] = useState([]);
    const [clientsData, setClientsData] = useState([]);
    const [clientsMap, setClientsMap] = useState({}); // Para acceso rápido por ID
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [chartMaxValue, setChartMaxValue] = useState(1000);
    const [isTableVisible, setIsTableVisible] = useState(false);

    // Refs para intervalos (evitar memory leaks)
    const pollIntervalRef = useRef(null);
    const pingIntervalRef = useRef(null);
    const isMountedRef = useRef(true);

    // Obtener token de autenticación
    const authToken = localStorage.getItem('token');

    // Función para actualizar clientes de manera estable (conservando referencias cuando es posible)
    const updateClientsData = useCallback((newClients) => {
        if (!newClients || !Array.isArray(newClients) || newClients.length === 0) return;

        setClientsData(prev => {
            // Crear un nuevo mapa de clientes para acceso rápido
            const updatedMap = {};

            // Iterar sobre los clientes existentes y actualizar sus datos
            const updated = prev.map(client => {
                const newData = newClients.find(c => c.id === client.id);
                if (newData) {
                    // Solo actualizar las propiedades específicas que cambian
                    updatedMap[client.id] = {
                        ...client,
                        rateDownload: newData.rateDownload,
                        rateUpload: newData.rateUpload,
                        downloadBytes: newData.downloadBytes,
                        uploadBytes: newData.uploadBytes
                    };
                    return updatedMap[client.id];
                }
                updatedMap[client.id] = client;
                return client;
            });

            // Agregar clientes nuevos que no estaban en la lista anterior
            const existingIds = new Set(prev.map(c => c.id));
            const newItems = newClients
                .filter(c => !existingIds.has(c.id))
                .map(c => {
                    updatedMap[c.id] = c;
                    return c;
                });

            // Actualizar el mapa de clientes para acceso rápido
            setClientsMap(updatedMap);

            // Devolver la lista combinada y ordenada
            return [...updated, ...newItems];
        });
    }, []);

    // Configuración de manejadores de eventos para el socket
    const socketEventHandlers = {
        'traffic:update': (data) => {
            if (!clientId || data.clientId === clientId) {
                console.log('Actualización de tráfico recibida:', data);

                // Actualizar datos en tiempo real
                setRealtimeData(data);

                // Agregar al historial manteniendo solo los últimos 30 puntos (para más fluidez visual)
                setTrafficHistory(prev => {
                    const newPoint = {
                        ...data,
                        time: new Date().toLocaleTimeString('es-ES', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                        })
                    };
                    const updated = [...prev, newPoint];

                    // Solo mantenemos los últimos 30 puntos para una visualización más fluida
                    const result = updated.slice(-30);

                    // Calcular nuevo valor máximo para la escala del gráfico
                    if (result.length > 0) {
                        const maxDownload = Math.max(...result.map(item =>
                            extractRateValue(item.rateDownload || '0')
                        ));
                        const maxUpload = Math.max(...result.map(item =>
                            extractRateValue(item.rateUpload || '0')
                        ));
                        const newMax = Math.max(maxDownload, maxUpload) * 1.2; // 20% más para espacio visual
                        setChartMaxValue(Math.max(newMax, 100)); // Al menos 100 para evitar escalas muy pequeñas
                    }

                    return result;
                });

                setLastUpdated(new Date());
            }
        },
        'traffic:all': (data) => {
            if (!clientId && data.clients && Array.isArray(data.clients)) {
                console.log('Datos de todos los clientes recibidos:', data.clients.length);

                // Actualizar datos de manera estable
                updateClientsData(data.clients);

                // Si la tabla aún no se ha mostrado, y tenemos datos, mostrarla
                if (!isTableVisible && data.clients.length > 0) {
                    setIsTableVisible(true);
                }

                setLastUpdated(new Date());
            }
        },
        'connect': () => {
            console.log('Socket conectado correctamente');
            setIsConnected(true);
        },
        'disconnect': () => {
            console.log('Socket desconectado');
            setIsConnected(false);
            startPolling(); // Iniciar polling como fallback
        },
        'error': (error) => {
            console.error('Error de socket:', error);
            setIsConnected(false);
            startPolling(); // Iniciar polling como fallback
        }
    };

    // Hook de socket estable
    const { socket, isConnected: socketConnected, emit } = useStableSocket({
        authToken,
        clientId,
        enabled: true,
        onConnect: () => {
            console.log('Socket conectado, solicitando datos iniciales');
            // Limpiar intervalo de polling al conectar el socket
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }

            // Limpiar ping previo
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }

            if (!isMountedRef.current) return; // No hacer nada si ya se desmontó

            setIsConnected(true);

            // Suscribirse a actualizaciones de tráfico
            emit('subscribe:traffic', clientId ? { clientId } : undefined);

            // Solicitar datos iniciales
            emit('get:traffic', clientId ? { clientId } : undefined);

            // Enviar un ping periódico para mantener la conexión activa
            pingIntervalRef.current = setInterval(() => {
                if (!isMountedRef.current) return;
                emit('ping');
                emit('get:traffic', clientId ? { clientId } : undefined);
            }, 3000);
        },
        onDisconnect: () => {
            console.log('Socket desconectado, iniciando polling como fallback');
            setIsConnected(false);
            startPolling();
        },
        onError: (error) => {
            console.error('Error en socket:', error);
            setError('No se pudo establecer conexión en tiempo real');
            setIsConnected(false);
            startPolling();
        },
        eventHandlers: socketEventHandlers
    });

    // Sincronizar el estado isConnected con el valor recibido del hook
    useEffect(() => {
        setIsConnected(socketConnected);
    }, [socketConnected]);

    // Función para iniciar polling como fallback
    const startPolling = useCallback(() => {
        // Limpiar intervalo existente
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }

        if (!isMountedRef.current) return; // No iniciar polling si ya se desmontó

        // Crear nuevo intervalo más frecuente (cada 3 segundos)
        pollIntervalRef.current = setInterval(() => {
            if (!isMountedRef.current) return;
            fetchTrafficData();
        }, 3000);
    }, [clientId]);

    // Limpiar TODOS los intervalos al desmontar el componente
    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            console.log('RealtimeTraffic: Limpiando intervalos al desmontar');
            isMountedRef.current = false;

            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }
        };
    }, []);

    // Función para cargar datos iniciales
    const fetchTrafficData = useCallback(async () => {
        try {
            setLoading(true);

            // Definir URL según contexto
            const url = clientId
                ? `/api/traffic/realtime/${clientId}`
                : '/api/traffic/current';

            const result = await get(url);

            if (clientId) {
                // Datos para un cliente específico
                if (result.current) {
                    setRealtimeData({
                        clientId,
                        rateDownload: result.current.download,
                        rateUpload: result.current.upload,
                        downloadBytes: result.total?.download || 0,
                        uploadBytes: result.total?.upload || 0
                    });
                }

                // Procesar historial
                if (result.history && Array.isArray(result.history)) {
                    const processedHistory = result.history.map(point => ({
                        time: point.time || new Date(point.epoch_time * 1000).toLocaleTimeString('es-ES', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                        }),
                        rateDownload: point.download,
                        rateUpload: point.upload
                    }));
                    setTrafficHistory(processedHistory);
                }
            } else {
                // Datos para todos los clientes
                if (result.clients && Array.isArray(result.clients)) {
                    // Actualizar datos de manera estable
                    updateClientsData(result.clients);

                    // Si la tabla aún no se ha mostrado, y tenemos datos, mostrarla
                    if (!isTableVisible && result.clients.length > 0) {
                        setIsTableVisible(true);
                    }
                }
            }

            setLastUpdated(new Date());
        } catch (error) {
            console.error('Error cargando datos de tráfico:', error);
            setError('Error al cargar datos de tráfico');
        } finally {
            setLoading(false);
        }
    }, [clientId, setError, updateClientsData, isTableVisible]);

    // Cargar datos iniciales al montar el componente
    useEffect(() => {
        fetchTrafficData();
    }, [fetchTrafficData]);

    // Formatear velocidad para mostrar
    const formatRate = (rate) => {
        if (!rate) return '0 Kbps';

        // Si es una cadena como "10M" o "1.5k"
        if (typeof rate === 'string') {
            const match = rate.match(/^([\d.]+)([KMGkmg])?$/i);
            if (match) {
                const [, value, unit = ''] = match;
                return `${value} ${unit.toUpperCase() || 'K'}bps`;
            }
            return rate;
        }

        // Si es un número
        if (rate >= 1024) {
            return `${(rate / 1024).toFixed(1)} Mbps`;
        }
        return `${rate.toFixed(1)} Kbps`;
    };

    // Formatear bytes para mostrar
    const formatBytes = (bytes) => {
        if (!bytes || isNaN(bytes)) return '0 B';

        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    // Extraer valores numéricos de formato de tasa y convertir a Kbps
    const extractRateValue = (rateStr) => {
        if (!rateStr) return 0;

        // Para cadenas como "10M" o "1.5k"
        const match = String(rateStr).match(/^([\d.]+)([KkMmGg])?$/);
        if (!match) return 0;

        const [, value, unit = ''] = match;
        const numValue = parseFloat(value);

        // Convertir a Kbps sin importar la unidad original
        switch (unit.toUpperCase()) {
            case 'K': return numValue;                  // Ya está en Kbps
            case 'M': return numValue * 1024;           // Mbps a Kbps
            case 'G': return numValue * 1024 * 1024;    // Gbps a Kbps
            default: return numValue / 1024;            // bps a Kbps
        }
    };

    // Formateador personalizado para valores en el tooltip del gráfico
    const formatChartValue = (value) => {
        if (value === 0) return '0 Kbps';

        // Convertir automáticamente entre Kbps y Mbps
        if (value >= 1024) {
            return `${(value / 1024).toFixed(2)} Mbps`;
        } else {
            return `${value.toFixed(2)} Kbps`;
        }
    };

    // Formateador personalizado para las etiquetas del eje Y
    const formatYAxisTick = (value) => {
        if (value === 0) return '0';

        // Simplificar etiquetas, la unidad ya aparece en la etiqueta del eje
        if (value >= 1024) {
            return `${(value / 1024).toFixed(1)}`;
        } else {
            return `${value.toFixed(0)}`;
        }
    };

    // Preparar datos para gráficos - Mantener en Kbps para consistencia
    const prepareChartData = () => {
        return trafficHistory.map(item => {
            // Extraer valores en Kbps para consistencia
            const downloadRate = extractRateValue(item.rateDownload || '0');
            const uploadRate = extractRateValue(item.rateUpload || '0');

            return {
                time: item.time || new Date().toLocaleTimeString(),
                downloadRate: downloadRate,  // Mantener en Kbps
                uploadRate: uploadRate,      // Mantener en Kbps
                // Añadir unidades solo para referencia, no para gráfico
                downloadRateFormatted: formatChartValue(downloadRate),
                uploadRateFormatted: formatChartValue(uploadRate)
            };
        });
    };

    const chartData = prepareChartData();

    // Determinar la unidad adecuada para el eje Y basado en el valor máximo
    const determineYAxisUnit = () => {
        if (chartMaxValue >= 1024) {
            return 'Mbps';
        } else {
            return 'Kbps';
        }
    };

    // Función para convertir valores al formato adecuado para el eje Y
    const getYAxisValues = () => {
        const unit = determineYAxisUnit();
        if (unit === 'Mbps') {
            return {
                unit,
                domain: [0, Math.ceil(chartMaxValue / 1024)],
                formatter: (value) => `${value}`
            };
        } else {
            return {
                unit,
                domain: [0, chartMaxValue],
                formatter: (value) => `${value}`
            };
        }
    };

    const yAxisInfo = getYAxisValues();

    // Recargar datos manualmente
    const handleRefresh = () => {
        fetchTrafficData();

        // Si hay conexión de socket, solicitar actualización también
        if (isConnected && socket) {
            emit('get:traffic', clientId ? { clientId } : undefined);
        }
    };

    // Determinar si mostrar lista de clientes o datos de cliente específico
    const showClientsList = !clientId && clientsData.length > 0;

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mb: 2
            }}>
                <Typography variant="h6">
                    Monitoreo de Tráfico en Tiempo Real
                    {lastUpdated && (
                        <Typography variant="body2" component="span" sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 0.5, sm: 0 }, color: 'text.secondary', display: { xs: 'block', sm: 'inline' } }}>
                            Actualizado: {lastUpdated.toLocaleTimeString()}
                        </Typography>
                    )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'flex-end' }, gap: 1.5, flexWrap: 'wrap' }}>
                    {isConnected ? (
                        <Chip
                            size="small"
                            color="success"
                            label="Conexión en tiempo real"
                        />
                    ) : (
                        <Chip
                            size="small"
                            color="warning"
                            label="Actualización periódica"
                        />
                    )}
                    <Button
                        startIcon={<RefreshIcon />}
                        variant="outlined"
                        onClick={handleRefresh}
                        disabled={loading}
                        size="small"
                    >
                        Actualizar
                    </Button>
                </Box>
            </Box>

            {loading && <LinearProgress sx={{ mb: 2 }} />}

            <Grid container spacing={3}>
                {/* Panel de vista de clientes activos - Con estabilidad en renderizado */}
                {showClientsList && (
                    <Grid item xs={12}>
                        <TableContainer
                            component={Paper}
                            sx={{
                                mb: 3,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                animation: isTableVisible ? 'none' : 'fadeIn 0.5s',
                                overflowX: 'auto',
                                '&::-webkit-scrollbar': {
                                    height: '6px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'rgba(0,0,0,0.15)',
                                    borderRadius: '4px',
                                }
                            }}
                        >
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: 'rgb(246, 248, 250)' }}>
                                    <TableRow>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell>IP</TableCell>
                                        <TableCell align="right">Download</TableCell>
                                        <TableCell align="right">Upload</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                        <TableCell align="center">Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {clientsData.length > 0 ? (
                                        clientsData.map((client) => (
                                            <ClientRow
                                                key={client.id}
                                                client={client}
                                                formatRate={formatRate}
                                                formatBytes={formatBytes}
                                                onViewDetails={onViewDetails}
                                            />
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                No hay datos de tráfico disponibles
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                )}

                {/* Panels de métricas en tiempo real (para cliente específico) */}
                {(realtimeData || clientId) && (
                    <>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <CardHeader
                                    title="Download"
                                    avatar={<Avatar sx={{ bgcolor: 'primary.main' }}><CloudDownload /></Avatar>}
                                />
                                <CardContent>
                                    <Typography variant="h4" component="div" align="center" fontWeight="500">
                                        {realtimeData ? formatRate(realtimeData.rateDownload) : '0 Kbps'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        {realtimeData ? formatBytes(realtimeData.downloadBytes) : '0 B'} transferidos
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <CardHeader
                                    title="Upload"
                                    avatar={<Avatar sx={{ bgcolor: 'success.main' }}><CloudUpload /></Avatar>}
                                />
                                <CardContent>
                                    <Typography variant="h4" component="div" align="center" fontWeight="500">
                                        {realtimeData ? formatRate(realtimeData.rateUpload) : '0 Kbps'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        {realtimeData ? formatBytes(realtimeData.uploadBytes) : '0 B'} transferidos
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card sx={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                <CardHeader
                                    title="Total"
                                    avatar={<Avatar sx={{ bgcolor: 'secondary.main' }}><BarChart /></Avatar>}
                                />
                                <CardContent>
                                    <Typography variant="h4" component="div" align="center" fontWeight="500">
                                        {realtimeData ? formatBytes(
                                            parseInt(realtimeData.downloadBytes || 0) + parseInt(realtimeData.uploadBytes || 0)
                                        ) : '0 B'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Tráfico total transferido
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                )}

                {/* Gráfico de velocidad en tiempo real (para cliente específico) */}
                {clientId && chartData.length > 0 && (
                    <Grid item xs={12}>
                        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)', minWidth: 0 }}>
                            <CardHeader
                                title="Velocidad en Tiempo Real"
                                sx={{ pb: 0 }}
                                titleTypographyProps={{ fontWeight: '500' }}
                            />
                            <CardContent>
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart
                                        data={chartData}
                                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis
                                            dataKey="time"
                                            tick={{ fontSize: 12, fill: '#666' }}
                                            tickMargin={8}
                                        />
                                        <YAxis
                                            yAxisId="left"
                                            label={{
                                                value: yAxisInfo.unit,
                                                angle: -90,
                                                position: 'insideLeft',
                                                fill: '#666',
                                                fontSize: 12
                                            }}
                                            tick={{ fontSize: 12, fill: '#666' }}
                                            tickFormatter={formatYAxisTick}
                                            domain={yAxisInfo.domain}
                                        />
                                        <RechartsTooltip
                                            formatter={(value, name) => {
                                                return [formatChartValue(value), name === 'downloadRate' ? 'Download' : 'Upload'];
                                            }}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                border: 'none',
                                                borderRadius: '4px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                            }}
                                            labelStyle={{ color: '#333', fontWeight: 'bold' }}
                                        />
                                        <Legend wrapperStyle={{ paddingTop: 10 }} />
                                        <Line
                                            yAxisId="left"
                                            type="monotone"
                                            dataKey="downloadRate"
                                            name="Download"
                                            stroke="#3f51b5"
                                            strokeWidth={2}
                                            dot={false}
                                            activeDot={{ r: 6, stroke: '#3f51b5', strokeWidth: 2, fill: '#fff' }}
                                        />
                                        <Line
                                            yAxisId="left"
                                            type="monotone"
                                            dataKey="uploadRate"
                                            name="Upload"
                                            stroke="#4caf50"
                                            strokeWidth={2}
                                            dot={false}
                                            activeDot={{ r: 6, stroke: '#4caf50', strokeWidth: 2, fill: '#fff' }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}

export default RealtimeTraffic;